import { useState, useRef, useCallback } from 'react'
import { businessTimeZone } from '../lib/businessTime'
import { motion, AnimatePresence } from 'framer-motion'
import {
  ShieldCheck,
  ShieldAlert,
  MapPin,
  Monitor,
  Smartphone,
  ChevronDown,
  ChevronUp,
  Clock,
} from 'lucide-react'
import type { Alert } from '../types'
import { cn, timeAgo } from '../lib/utils'
import { useAlertStore } from '../stores/alertStore'
import { EmptyState } from './EmptyState'

interface AlertTimelineProps {
  alerts: Alert[]
  isLoading?: boolean
  onRefresh?: () => Promise<void>
}

function getSeverityIcon(severity?: string) {
  switch (severity) {
    case 'critical':
      return <ShieldAlert className="w-5 h-5 text-danger" aria-hidden="true" />
    case 'high':
      return <ShieldAlert className="w-5 h-5 text-warning" aria-hidden="true" />
    case 'medium':
      return <ShieldCheck className="w-5 h-5 text-info" aria-hidden="true" />
    case 'low':
      return <ShieldCheck className="w-5 h-5 text-success" aria-hidden="true" />
    default:
      return <ShieldCheck className="w-5 h-5 text-gray-400" aria-hidden="true" />
  }
}

function getSeverityDot(severity?: string) {
  switch (severity) {
    case 'critical':
      return 'bg-danger'
    case 'high':
      return 'bg-warning'
    case 'medium':
      return 'bg-info'
    case 'low':
      return 'bg-success'
    default:
      return 'bg-gray-400'
  }
}

function DeviceIcon({ device }: { device?: string }) {
  if (!device) return null
  const isMobile = device.toLowerCase().includes('mobile') || device.toLowerCase().includes('phone')
  return isMobile ? (
    <Smartphone className="w-3 h-3" aria-hidden="true" />
  ) : (
    <Monitor className="w-3 h-3" aria-hidden="true" />
  )
}

function TimelineItem({ alert, index }: { alert: Alert; index: number }) {
  const [expanded, setExpanded] = useState(false)
  const { dismissAlert } = useAlertStore()

  const handleDismiss = useCallback(async () => {
    await dismissAlert(alert.id)
  }, [alert.id, dismissAlert])

  const isResolved = alert.status === 'approved' || alert.status === 'rejected' || alert.status === 'dismissed'
  const requiresApproval = alert.requires_approval && !isResolved
  const severity = alert.severity ?? 'medium'

  return (
    <motion.li
      layout
      initial={{ opacity: 0, y: 20, scale: 0.97 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, x: -40, scale: 0.95 }}
      transition={{ duration: 0.3, delay: index * 0.05, ease: 'easeOut' }}
      className={cn(
        'relative pl-6 pb-6 border-l-2 last:border-l-transparent',
        isResolved ? 'border-gray-200' : 'border-brass/40',
      )}
      role="listitem"
    >
      {/* Status dot */}
      <div
        className={cn(
          'absolute -left-[5px] top-1 w-2.5 h-2.5 rounded-full ring-2 ring-cream',
          isResolved ? 'bg-gray-300' : getSeverityDot(severity),
        )}
        aria-hidden="true"
      />

      {/* Card */}
      <div
        className={cn(
          'rounded-xl border p-4 transition-all',
          isResolved
            ? 'bg-gray-50 border-gray-200 opacity-70'
            : 'bg-white border-gray-200 shadow-sm',
        )}
      >
        {/* Top row: icon + headline */}
        <div className="flex items-start gap-3">
          <div className="mt-0.5 flex-shrink-0">{getSeverityIcon(severity)}</div>
          <div className="flex-1 min-w-0">
            <p className={cn('text-sm font-medium leading-snug', isResolved ? 'text-gray-500' : 'text-navy')}>
              {alert.headline}
            </p>

            {/* Action taken */}
            {alert.action_taken && !isResolved && (
              <p className="mt-1 text-xs text-gray-600 italic">{alert.action_taken}</p>
            )}

            {/* CTA */}
            {alert.cta && !isResolved && (
              <p className="mt-1 text-xs font-medium text-warning">{alert.cta}</p>
            )}

            {/* Meta line */}
            <div className="mt-2 flex items-center gap-2 text-xs text-gray-400 flex-wrap">
              <span className="flex items-center gap-0.5">
                <Clock className="w-3 h-3" aria-hidden="true" />
                <time dateTime={alert.created_at}>{timeAgo(alert.created_at)}</time>
              </span>
              {alert.location && (
                <span className="flex items-center gap-0.5">
                  <MapPin className="w-3 h-3" aria-hidden="true" />
                  {alert.location}
                </span>
              )}
              {alert.device && (
                <span className="flex items-center gap-0.5">
                  <DeviceIcon device={alert.device} />
                  {alert.device}
                </span>
              )}
              {isResolved && (
                <span
                  className={cn(
                    'px-1.5 py-0.5 rounded-full text-[10px] font-medium capitalize',
                    alert.status === 'approved'
                      ? 'bg-success-light text-success'
                      : alert.status === 'rejected'
                        ? 'bg-danger-light text-danger'
                        : 'bg-gray-100 text-gray-500',
                  )}
                >
                  {alert.status}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Expand/collapse + action buttons */}
        <div className="mt-3 flex items-center gap-2">
          <button
            type="button"
            onClick={() => setExpanded(!expanded)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                setExpanded(!expanded)
              }
            }}
            className="tap-min flex items-center gap-1 text-xs text-gray-500 hover:text-brass transition-colors px-2 py-1 rounded-md"
            aria-expanded={expanded}
            aria-label={expanded ? 'Collapse details' : 'Expand details'}
          >
            {expanded ? (
              <>
                <ChevronUp className="w-3 h-3" aria-hidden="true" /> Less
              </>
            ) : (
              <>
                <ChevronDown className="w-3 h-3" aria-hidden="true" /> Details
              </>
            )}
          </button>

          {/* Response is disarmed — this surface executes nothing. */}
          {requiresApproval && (
            <p className="ml-auto max-w-[14rem] text-right text-xs text-gray-500 leading-snug">
              The team decides next steps. This surface executes nothing.
            </p>
          )}

          {/* Dismiss button for resolved */}
          {isResolved && (
            <button
              type="button"
              onClick={handleDismiss}
              className="ml-auto tap-min text-xs text-gray-400 hover:text-gray-600 px-2 py-1 rounded-md transition-colors"
              aria-label="Dismiss alert"
            >
              Dismiss
            </button>
          )}
        </div>

        {/* Expanded content */}
        <AnimatePresence>
          {expanded && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="overflow-hidden"
            >
              <div className="mt-3 pt-3 border-t border-gray-100 text-xs text-gray-600 space-y-1">
                <p>
                  <span className="font-medium">Alert ID:</span> {alert.id}
                </p>
                {alert.case_id && (
                  <p>
                    <span className="font-medium">Case:</span> {alert.case_id}
                  </p>
                )}
                <p>
                  <span className="font-medium">Created:</span>{' '}
                  {new Date(alert.created_at).toLocaleString('en-US', { timeZone: businessTimeZone() })}
                </p>
                {alert.action_taken && (
                  <p>
                    <span className="font-medium">Action:</span> {alert.action_taken}
                  </p>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.li>
  )
}

export function AlertTimeline({ alerts, isLoading, onRefresh }: AlertTimelineProps) {
  const [isRefreshing, setIsRefreshing] = useState(false)
  const touchStart = useRef<number>(0)
  const containerRef = useRef<HTMLDivElement>(null)

  // Pull-to-refresh
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    if (containerRef.current && containerRef.current.scrollTop === 0) {
      touchStart.current = e.touches[0].clientY
    }
  }, [])

  const handleTouchEnd = useCallback(
    async (e: React.TouchEvent) => {
      if (!onRefresh) return
      const touchEnd = e.changedTouches[0].clientY
      const pullDistance = touchEnd - touchStart.current
      if (pullDistance > 80 && containerRef.current && containerRef.current.scrollTop === 0) {
        setIsRefreshing(true)
        await onRefresh()
        setIsRefreshing(false)
      }
    },
    [onRefresh],
  )

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-16 gap-3">
        <div className="w-8 h-8 border-2 border-brass border-t-transparent rounded-full animate-spin" />
        <p className="text-sm text-gray-500">Loading alerts...</p>
      </div>
    )
  }

  return (
    <div
      ref={containerRef}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      className="relative"
    >
      {/* Pull-to-refresh indicator */}
      <AnimatePresence>
        {isRefreshing && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="flex items-center justify-center py-3"
          >
            <div className="w-5 h-5 border-2 border-brass border-t-transparent rounded-full animate-spin" />
            <span className="ml-2 text-xs text-gray-500">Refreshing...</span>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Timeline list */}
      {alerts.length === 0 ? (
        <EmptyState
          icon={ShieldCheck}
          title="All Clear"
          message="Nothing is queued. Quiet is quiet — this view never fabricates events."
        />
      ) : (
        <ul className="relative ml-2" role="list" aria-label="Alert timeline">
          <AnimatePresence mode="popLayout" initial={false}>
            {alerts.map((alert, index) => (
              <TimelineItem key={alert.id} alert={alert} index={index} />
            ))}
          </AnimatePresence>
        </ul>
      )}
    </div>
  )
}
